import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';

import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

function Accordion({ ...props }: ComponentProps<typeof Root>) {
  return <Root data-slot="accordion" {...props} />;
}

function AccordionItem({ className, ...props }: ComponentProps<typeof Item>) {
  return (
    <Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  disabled,
  ...props
}: ComponentProps<typeof Trigger>) {
  return (
    <Header className="flex">
      <Trigger
        data-slot="accordion-trigger"
        className={cn(
          'cursor-pointer font-semibold focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
          className
        )}
        disabled={disabled}
        {...props}
      >
        {children}
        {!disabled && (
          <ChevronDownIcon className="text-muted-foreground pointer-events-none size-5 shrink-0 translate-y-0.5 transition-transform duration-200" />
        )}
      </Trigger>
    </Header>
  );
}

function AccordionContent({ className, children, ...props }: ComponentProps<typeof Content>) {
  return (
    <Content
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </Content>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
